/** An expression / template field with insert-token chips and a live check.

    Conditions (`when`, `next[].when`, `enabled_when`, `expect`, `loop.until`,
    `set.expr` …) are written in the platform's ONE expression language — the
    one item_change fills and validation rules use — so they are checked here by
    the same evaluator the runner runs (`@twasta/page-runtime/expr`). Tokens are
    bound to plain names first, exactly as the runtime rewrites them, so what
    passes here is what parses there.

    Templates (`set.value`, `with`, `params`, titles, messages) are text with
    `{tokens}`; they are checked for balanced braces and for names the model
    does not have. */

import { useMemo, useRef, useState } from 'react'
import { Braces } from 'lucide-react'
import { ExprError, Vector, bindReferences, evaluate } from '@twasta/page-runtime/expr'
import { inputStyle, monoInputStyle } from './ui'
import type { TokenGroup } from './context'
import { tokenGroups, useWizardDesign } from './context'

const KNOWN_SCOPES = /^(header|line|lines|form\d+|lines\d+|line\d+|var|input|step|scan|result|run|call|user|today|now)$/i

/** `{scan.30|1}` → `{scan.30}` (the fallback is not part of the language). */
function stripDefaults(src: string): string {
  return src.replace(/\{([^{}|]+)\|[^{}]*\}/g, '{$1}')
}

/** Parse + dry-run an expression with every token blank. null = fine. */
export function checkExpression(src: string): string | null {
  const text = stripDefaults(String(src || '').trim())
  if (!text) return null
  const { src: bound, refs } = bindReferences(text)
  // Braces left over OUTSIDE string literals are tokens the binder could not
  // read; inside a string (a regex quantifier like {2}) they are just text.
  const outsideStrings = bound.replace(/'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g, '')
  if (/[{}]/.test(outsideStrings)) return 'A {token} here is not one the language can read.'
  // A blank token may be compared, added or measured; try the three blanks a
  // value can be before deciding the expression itself is wrong.
  let first: string | null = null
  for (const blank of [null, 0, ''] as unknown[]) {
    const variables: Record<string, unknown> = {}
    refs.forEach((ref, i) => {
      variables[`_r${i}`] = /^lines\d*\./i.test(ref) ? Vector.of2([]) : blank
    })
    try {
      evaluate(bound, { variables })
      return null
    } catch (err) {
      if (first === null) first = err instanceof ExprError || err instanceof Error ? err.message : String(err)
    }
  }
  return first
}

/** Unknown names / unbalanced braces in a template. null = fine. */
export function checkTemplate(src: string, known: Set<string>): string | null {
  const text = String(src ?? '')
  let depth = 0
  for (const ch of text) {
    if (ch === '{') depth += 1
    else if (ch === '}') depth -= 1
    if (depth < 0 || depth > 1) return 'Unbalanced { }.'
  }
  if (depth !== 0) return 'Unbalanced { }.'
  const unknown: string[] = []
  for (const m of text.matchAll(/\{([^{}|]+)(?:\|[^{}]*)?\}/g)) {
    const tok = m[1].trim()
    const scope = tok.split('.')[0]
    if (!KNOWN_SCOPES.test(scope)) { unknown.push(tok); continue }
    // Columns / vars / inputs / steps the model can name are checked; scan,
    // result, call and run values only exist at run time.
    if (/^(header|line|lines|var|input|step)$/i.test(scope) && known.size > 0) {
      const norm = scope.toLowerCase() === 'step'
        ? `{step.${tok.split('.')[1] || ''}.value}`
        : `{${tok}}`
      if (!known.has(norm.toLowerCase())) unknown.push(tok)
    }
  }
  return unknown.length ? `Not in this wizard: ${unknown.map((u) => `{${u}}`).join(', ')}` : null
}

export default function ExprInput({
  value, onChange, mode = 'expr', placeholder, multiline, formNo, extraTokens, disabled,
}: {
  value: unknown
  onChange: (v: string) => void
  /** 'expr' — a condition / calculation; 'template' — text with {tokens}. */
  mode?: 'expr' | 'template'
  placeholder?: string
  multiline?: boolean
  /** The step's own form_no (what {line.X} reads). */
  formNo?: string
  /** Step-local tokens ({scan.X}, {call.outcome} …). */
  extraTokens?: TokenGroup[]
  disabled?: boolean
}) {
  const ctx = useWizardDesign()
  const text = value === undefined || value === null ? ''
    : typeof value === 'string' ? value : JSON.stringify(value)
  const [chipsOpen, setChipsOpen] = useState(false)
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  const groups = useMemo(() => tokenGroups(ctx, formNo, extraTokens), [ctx, formNo, extraTokens])
  const known = useMemo(() => {
    const s = new Set<string>()
    for (const g of groups) for (const t of g.tokens) s.add(t.toLowerCase())
    return s
  }, [groups])
  const problem = useMemo(
    () => (mode === 'expr' ? checkExpression(text) : checkTemplate(text, known)),
    [mode, text, known],
  )

  const insert = (tok: string) => {
    const el = ref.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const next = text.slice(0, start) + tok + text.slice(end)
    onChange(next)
    requestAnimationFrame(() => {
      if (!el) return
      el.focus()
      const at = start + tok.length
      el.setSelectionRange(at, at)
    })
  }

  const style = { ...(mode === 'expr' ? monoInputStyle : inputStyle), paddingRight: 30 }
  return (
    <div>
      <div style={{ position: 'relative' }}>
        {multiline ? (
          <textarea ref={ref} value={text} disabled={disabled || ctx.readOnly} placeholder={placeholder}
            spellCheck={false} onChange={(e) => onChange(e.target.value)}
            style={{ ...style, minHeight: 60, resize: 'vertical' }} />
        ) : (
          <input ref={ref} value={text} disabled={disabled || ctx.readOnly} placeholder={placeholder}
            spellCheck={false} onChange={(e) => onChange(e.target.value)} style={style} />
        )}
        <button type="button" title="Insert a value" aria-label="Insert a value"
          onClick={() => setChipsOpen((o) => !o)}
          style={{
            position: 'absolute', right: 3, top: 3, width: 22, height: 22, border: 'none',
            borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center',
            background: chipsOpen ? 'var(--color-accent)' : 'transparent',
            color: chipsOpen ? '#fff' : 'var(--color-text-secondary)',
          }}>
          <Braces size={13} />
        </button>
      </div>
      {problem && text.trim() && (
        <div style={{
          fontSize: 13, marginTop: 3,
          color: mode === 'expr' ? 'var(--color-error, #d33)' : 'var(--color-warning, #b45309)',
        }}>
          {problem}
        </div>
      )}
      {chipsOpen && (
        <div style={{
          marginTop: 4, padding: 6, maxHeight: 180, overflowY: 'auto',
          border: '1px solid var(--color-border)', borderRadius: 4,
          background: 'var(--color-bg-secondary)',
        }}>
          {groups.map((g) => (
            <div key={g.label} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 13, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 2 }}>
                {g.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {g.tokens.map((t) => (
                  <button key={t} type="button" onClick={() => insert(t)} style={{
                    fontFamily: 'monospace', fontSize: 13, padding: '1px 6px', borderRadius: 9,
                    border: '1px solid var(--color-border)', background: 'var(--color-bg)',
                    color: 'var(--color-text)', cursor: 'pointer',
                  }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
